/**
 * @file lazyWithReload.js
 * React.lazy con recupero automatico dei chunk obsoleti dopo un deploy.
 *
 * CONTESTO
 * Le pagine dell'app sono caricate "a richiesta" (React.lazy + import dinamico):
 * Vite mette ogni pagina in un file separato con un hash nel nome,
 * es. /assets/MergePage-DUIQi5Rt.js, che il browser scarica solo la prima volta
 * che la pagina viene visitata.
 *
 * PROBLEMA
 * A ogni rilascio gli hash cambiano e il nuovo container contiene solo i file nuovi.
 * Una scheda aperta PRIMA del deploy conosce ancora i nomi vecchi: quando l'utente
 * naviga verso una pagina non ancora visitata, il browser chiede un file che non
 * esiste piu' e l'import fallisce con
 *   "TypeError: Failed to fetch dynamically imported module: .../assets/XxxPage-<hash>.js"
 * React Router intercetta l'errore e mostra "Unexpected Application Error!".
 *
 * SOLUZIONE
 * - lazyWithReload: si usa al posto di React.lazy. Se l'import della pagina fallisce,
 *   ricarica l'intera pagina: il browser scarica l'index.html aggiornato, che punta
 *   ai chunk della nuova versione, e la navigazione riprende sull'URL corrente.
 * - reloadOnceForStaleChunk: esegue la ricarica al massimo una volta ogni
 *   RELOAD_WINDOW_MS. Se l'errore si ripete subito dopo la ricarica, non dipende
 *   da un deploy (es. rete assente, server giu'): l'errore viene propagato e
 *   mostrato normalmente, invece di entrare in un loop di ricariche infinite.
 *
 * Copre anche il CSS delle pagine: se Vite non riesce a scaricarlo, l'errore arriva
 * allo stesso catch. Per l'export Excel (import di exceljs) vedi invece loadExcelAlfa.js,
 * che mostra un avviso senza ricaricare.
 */

import { lazy } from 'react';

// Chiave in sessionStorage con il timestamp dell'ultima ricarica automatica.
// sessionStorage e' per-scheda e per-dominio: test e produzione non si influenzano.
const RELOAD_FLAG = 'chunk-reload-at';
// Finestra entro cui una seconda ricarica viene considerata un loop e bloccata.
const RELOAD_WINDOW_MS = 10_000;

/**
 * Ricarica la pagina per recuperare la versione aggiornata dell'app,
 * a meno che non sia gia' stato fatto negli ultimi RELOAD_WINDOW_MS.
 * @returns {boolean} true se la ricarica e' stata avviata, false se bloccata (anti-loop).
 */
export function reloadOnceForStaleChunk() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage non disponibile (es. navigazione privata restrittiva):
    // ricarichiamo comunque, senza protezione anti-loop.
  }
  window.location.reload();
  return true;
}

/**
 * Sostituto di React.lazy che gestisce i chunk non piu' presenti dopo un deploy.
 * Uso: const MyPage = lazyWithReload(() => import('./pages/MyPage'));
 * @param {() => Promise<{ default: React.ComponentType }>} importer import dinamico del componente
 */
export function lazyWithReload(importer) {
  return lazy(() =>
    importer().catch((err) => {
      // Se la ricarica e' partita restituiamo una Promise che non si risolve mai:
      // Suspense continua a mostrare "Caricamento..." finche' il browser ricarica,
      // evitando che la schermata di errore compaia per un istante.
      if (reloadOnceForStaleChunk()) return new Promise(() => {});
      // Ricarica gia' tentata di recente: errore reale, lo lasciamo gestire a React Router.
      throw err;
    })
  );
}
